import { Badge, Grid, IconButton, Tooltip } from '@material-ui/core'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import WarningIcon from '@material-ui/icons/Warning'
import styles from 'assets/jss/material-dashboard-pro-react/components/exceptionFieldsStyle'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import { InvoiceProcess } from 'utils/Constants'
import { checkErrorOrWarning, getInvoiceProcess } from 'utils/functions'
import { generateJsonValidation } from 'utils/validator'
import { getProcessLookUp } from 'views/InvoiceException/utils'

const useStyles = makeStyles(styles)

const useStylesSelect = makeStyles(theme => ({
  selectBorder: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'green'
    }
  },
  inputWarning: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#f3bc07 !important'
    }
  },
  inputSuccess: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'green !important'
    }
  },
  inputError: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'red !important'
    }
  }
}))

const SelectProcess = props => {
  const {
    summaryField,
    control,
    index,
    rules,
    label,
    validationsError,
    setValidationErrorsState,
    validationErrorsState,
    appConfig,
    processList
  } = props
  let validationErrorMessages = validationErrorsState[summaryField.type]?.messages ?? ''
  let validated = validationErrorsState[summaryField.type]?.valid ?? null
  const [typeMsg, setTypeMsg] = useState(checkErrorOrWarning(validationErrorMessages, validated))
  const [invoiceProcess] = useState(() => getInvoiceProcess(appConfig))

  const [typeMsgFirst, setTypeMsgFirst] = useState(checkErrorOrWarning(validationsError))
  const [isValid, setIsValid] = useState(false)
  const [initialErrorMessages] = useState(validationsError)
  const [updatedValue, setUpdatedValue] = useState(summaryField.value)
  const [initialValue] = useState(summaryField.value)

  const classes = useStyles()
  const classesSelect = useStylesSelect()

  function validationMessages(validationsError) {
    if (!validationsError) {
      return ''
    }

    if (Array.isArray(validationsError)) {
      return (
        <>
          {validationsError.map((message, index) => (
            <div key={index}>{message.message}</div>
          ))}
        </>
      )
    }

    const messages = validationsError.split('\n')

    return (
      <>
        {messages.map((message, index) => (
          <div key={index}>{message}</div>
        ))}
      </>
    )
  }

  const changeValidationToSuccess = useCallback(() => {
    setTypeMsg('')
    setIsValid(true)
    setValidationErrorsState(old => {
      const previousValidation = old[summaryField.type] ?? {}
      return {
        ...old,
        [summaryField.type]: {
          ...previousValidation,
          messages: [{ type: 'success', message: 'Accepted by User' }],
          valid: true
        }
      }
    })
  }, [setValidationErrorsState, summaryField.type])

  const changeValidationToWarning = useCallback(
    initialErrorMessages => {
      setTypeMsg('warning')
      setTypeMsgFirst('warning')
      setIsValid(false)
      setValidationErrorsState(old => {
        const previousValidation = old[summaryField.type] ?? {}
        return {
          ...old,
          [summaryField.type]: {
            ...previousValidation,
            messages: initialErrorMessages,
            valid: null
          }
        }
      })
    },
    [setValidationErrorsState, summaryField.type]
  )

  const changeValidationToError = useCallback(
    initialErrorMessages => {
      setTypeMsg('error')
      setTypeMsgFirst('error')
      setIsValid(false)
      setValidationErrorsState(old => {
        const previousValidation = old[summaryField.type] ?? {}
        return {
          ...old,
          [summaryField.type]: {
            ...previousValidation,
            messages: initialErrorMessages,
            valid: null
          }
        }
      })
    },
    [setValidationErrorsState, summaryField.type]
  )

  useEffect(() => {
    if (initialValue !== updatedValue) {
      if (typeMsgFirst !== '') {
        changeValidationToSuccess()
      }
    } else {
      if (typeMsgFirst === 'error') {
        changeValidationToError(initialErrorMessages)
      } else if (typeMsgFirst === 'warning') {
        if (isValid || validated) {
          changeValidationToSuccess()
        } else {
          changeValidationToWarning(initialErrorMessages)
        }
      }
    }
  }, [
    updatedValue,
    changeValidationToError,
    changeValidationToWarning,
    changeValidationToSuccess,
    initialValue,
    isValid,
    typeMsgFirst,
    initialErrorMessages,
    validated
  ])

  function getColors(isDirty, typeMsg, error) {
    let color = ''
    let inputColor = ''
    let badgeColor = ''
    let tooltipColor = ''

    if (typeMsg === 'error') {
      color = classes.errorColor
      inputColor = classesSelect.inputError
      tooltipColor = classes.tooltipError
    }

    if (validated) {
      color = classes.successColor
      inputColor = classesSelect.inputSuccess
    }

    if (isValid && !Boolean(error)) {
      color = classes.successColor
      inputColor = classesSelect.inputSuccess
    }

    if (isDirty && !Boolean(error)) {
      color = classes.successColor
      inputColor = classesSelect.inputSuccess
    }

    if (!isDirty && typeMsg === 'warning') {
      color = classes.warningColor
      inputColor = classesSelect.inputWarning
      tooltipColor = classes.tooltipWarning
    }

    return { color, inputColor, badgeColor, tooltipColor }
  }

  function getSelectOptions(process) {
    const jsxResult = []

    const processLookUp = getProcessLookUp(processList, process)

    // eslint-disable-next-line
    for (const [key, value] of Object.entries(processLookUp)) {
      if (value === InvoiceProcess['ocr-global']) {
        jsxResult.push(
          <MenuItem value={key} key={key} disabled={true}>
            <em>{value}</em>
          </MenuItem>
        )
      } else {
        jsxResult.push(
          <MenuItem value={key} key={key}>
            {value}
          </MenuItem>
        )
      }
    }

    if (jsxResult.length > 0) {
      return jsxResult
    } else {
      return null
    }
  }

  return (
    <Controller
      render={({ field, fieldState: { error, isDirty } }) => {
        const isWarning = !isDirty && typeMsg === 'warning' && !Boolean(error)
        const isVisible =
          (Boolean(error) && typeMsg === 'error') || (!isDirty && typeMsg === 'error') || isWarning
        setUpdatedValue(field.value)
        return (
          <Grid container justifyContent="center">
            <Grid item xs={typeMsgFirst === 'warning' && !Boolean(error) && !isDirty ? 11 : 12}>
              <Tooltip
                classes={{
                  arrow: isWarning ? classes.warningColor : classes.errorColor,
                  tooltip: isWarning ? classes.tooltipWarning : classes.tooltipError
                }}
                title={isVisible ? validationMessages(validationErrorMessages) : ''}
                arrow
                placement="right"
              >
                <Badge
                  style={{ width: '95%' }}
                  classes={{
                    badge: isWarning ? classes.badgeWarning : classes.badgeError
                  }}
                  badgeContent={
                    isWarning ? <WarningIcon className={classes.warningColor}></WarningIcon> : '!'
                  }
                  invisible={isVisible ? false : true}
                >
                  <FormControl
                    variant="outlined"
                    error={Boolean(error) || typeMsg === 'error'}
                    style={{ width: '100%' }}
                  >
                    <InputLabel
                      id="demo-simple-select-outlined-label"
                      classes={{
                        root: !Boolean(error) ? getColors(isDirty, typeMsg, error).color : '',
                        focused: !Boolean(error) ? getColors(isDirty, typeMsg, error).color : ''
                      }}
                    >
                      {label}
                    </InputLabel>
                    <Select
                      {...field}
                      id={summaryField.id}
                      style={{ width: '100%' }}
                      labelId="demo-simple-select-label"
                      label={label ?? ''}
                      className={
                        !Boolean(error) ? getColors(isDirty, typeMsg, error).inputColor : ''
                      }
                      onChange={e => {
                        field.onChange(e.target.value)
                        setTypeMsg('')
                      }}
                    >
                      {getSelectOptions(invoiceProcess)}
                    </Select>
                  </FormControl>
                </Badge>
              </Tooltip>
            </Grid>
            {typeMsgFirst === 'warning' && !Boolean(error) && !isDirty ? (
              <Grid item xs={1} style={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton
                  style={{ padding: '0' }}
                  className={classes.buttonSuccess}
                  onClick={() =>
                    isValid || validated
                      ? changeValidationToWarning(initialErrorMessages)
                      : changeValidationToSuccess()
                  }
                >
                  {isValid || validated ? (
                    <CheckCircleIcon className={classes.successColor} />
                  ) : (
                    <CheckCircleOutlineIcon className={classes.successColor} />
                  )}
                </IconButton>
              </Grid>
            ) : null}
          </Grid>
        )
      }}
      name={`summaryFields.${index}.value`}
      control={control}
      rules={generateJsonValidation(rules, summaryField.type)}
    />
  )
}

export default SelectProcess
